import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import AzureGraphService from '../services/azureGraphService';
import MOMAuthService from '../services/momAuthService';
import './MOMGeneratorModal.css';

const MOMGeneratorModalFull = ({ isOpen, onClose, cycleId, employeeEmail, managerEmail }) => {
  const [inputMode, setInputMode] = useState('meeting_id');
  const [meetingId, setMeetingId] = useState('');
  const [transcriptText, setTranscriptText] = useState('');
  const [transcriptFile, setTranscriptFile] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [momAuthService] = useState(() => new MOMAuthService());
  const [isMOMAuthenticated, setIsMOMAuthenticated] = useState(false);

  useEffect(() => {
    // Check if already authenticated for MOM
    setIsMOMAuthenticated(momAuthService.isAuthenticated());
    
    // Handle auth callback from URL
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (code && state === 'mom-auth') {
      handleMOMAuthCallback(code);
      // Clean URL immediately
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [momAuthService]);

  const handleMOMAuthCallback = async (code) => {
    try {
      setError('');
      console.log('Processing MOM auth callback with code:', code.substring(0, 20) + '...');
      
      // Send code to backend for token exchange
      const response = await apiService.postData('exchange-mom-token', {
        code: code,
        redirect_uri: momAuthService.redirectUri
      });
      
      if (response.access_token) {
        // Store token from backend response
        sessionStorage.setItem('mom_access_token', response.access_token);
        setIsMOMAuthenticated(true);
        console.log('MOM authentication successful');
        
        // Auto-generate MOM after successful authentication
        if (meetingId.trim()) {
          handleGenerate();
        }
      } else {
        throw new Error('No access token received from backend');
      }
    } catch (error) {
      console.error('MOM authentication failed:', error);
      setError('MOM authentication failed: ' + error.message);
    }
  };

  const handleMOMLogin = async () => {
    if (!meetingId.trim()) {
      setError('Please enter a meeting ID or URL first');
      return;
    }
    
    try {
      setError('');
      const authUrl = momAuthService.getMOMAuthUrl();
      
      // Open popup for authentication
      const popup = window.open(
        authUrl,
        'momAuth',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );
      
      // Listen for popup messages
      const handleMessage = (event) => {
        console.log('Received message:', event.data);
        
        if (event.data.type === 'MOM_AUTH_SUCCESS') {
          const { code } = event.data;
          handleMOMAuthCallback(code);
          popup.close();
          window.removeEventListener('message', handleMessage);
        } else if (event.data.type === 'MOM_AUTH_ERROR') {
          setError('Authentication failed: ' + event.data.error);
          popup.close();
          window.removeEventListener('message', handleMessage);
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      // Check if popup was closed manually
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
        }
      }, 1000);
      
    } catch (error) {
      setError('Failed to open authentication popup: ' + error.message);
    }
  };

  const handleMOMLogout = () => {
    momAuthService.logout();
    setIsMOMAuthenticated(false);
  };

  const extractMeetingIdFromUrl = (url) => {
    // Extract meeting ID from Teams meeting URL
    const patterns = [
      /meetup-join\/([^\/\?]+)/,
      /19:meeting_([^@]+)/,
      /threadId=([^&]+)/,
      /meet\/([^\/\?]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return url; // Return as-is if no pattern matches
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setTranscriptFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setTranscriptText(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const extractTranscriptFromTeams = async (meetingUrl) => {
    try {
      const accessToken = momAuthService.getMOMAccessToken();
      if (!accessToken) {
        throw new Error('Please authenticate with Teams first');
      }

      const graphService = new AzureGraphService(accessToken);

      // Resolve URL or numeric join ID to Graph's online meeting ID (required for transcripts API)
      const resolvedMeetingId = await graphService.resolveOnlineMeetingId(meetingUrl);
      console.log('Resolved Graph meeting ID:', resolvedMeetingId);

      // Get transcripts for the meeting
      const transcripts = await graphService.listMeetingTranscripts(resolvedMeetingId);
      console.log('Available transcripts:', transcripts);

      if (transcripts.length === 0) {
        throw new Error('No transcripts found for this meeting. Ensure transcription was enabled and the meeting has ended.');
      }

      // Get the first transcript content
      const transcriptContent = await graphService.getTranscriptContent(resolvedMeetingId, transcripts[0].id);
      return transcriptContent;
    } catch (error) {
      console.error('Error extracting transcript from Teams:', error);
      throw error;
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError('');
    setResult(null);

    try {
      let requestData = {
        // Optional context for backend to cache MOM in tbl_EmployeeReviewAverages
        cycle_id: cycleId,
        employee_email: employeeEmail,
        manager_email: managerEmail,
      };
      
      if (inputMode === 'meeting_id') {
        if (!meetingId.trim()) {
          throw new Error('Please enter a meeting ID or URL');
        }
        
        // Use token as source of truth (state may not have updated yet after callback)
        const hasToken = momAuthService.getMOMAccessToken();
        if (!hasToken) {
          throw new Error('Please authenticate with Teams first to access meeting transcripts');
        }

        // Try to extract real transcript from Teams
        const realTranscript = await extractTranscriptFromTeams(meetingId.trim());
        requestData = { 
          ...requestData,
          transcript_text: realTranscript 
        };
      } else {
        if (!transcriptText.trim()) {
          throw new Error('Please provide transcript text');
        }
        requestData = { 
          ...requestData,
          transcript_text: transcriptText.trim() 
        };
      }

      const response = await apiService.postData('generate-mom-full', requestData);
      setResult(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReset = () => {
    setMeetingId('');
    setTranscriptText('');
    setTranscriptFile(null);
    setResult(null);
    setError('');
    setInputMode('meeting_id');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content mom-modal">
        <div className="modal-header">
          <h2>Generate Minutes of Meeting</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {!result ? (
            <>
              <div className="input-mode-selector">
                <label>
                  <input
                    type="radio"
                    value="meeting_id"
                    checked={inputMode === 'meeting_id'}
                    onChange={(e) => setInputMode(e.target.value)}
                  />
                  Meeting ID/URL
                </label>
                <label>
                  <input
                    type="radio"
                    value="transcript"
                    checked={inputMode === 'transcript'}
                    onChange={(e) => setInputMode(e.target.value)}
                  />
                  Upload Transcript
                </label>
              </div>

              {inputMode === 'meeting_id' ? (
                <div className="input-section">
                  <label htmlFor="meetingId">Teams Meeting URL:</label>
                  <input
                    id="meetingId"
                    type="text"
                    value={meetingId}
                    onChange={(e) => setMeetingId(e.target.value)}
                    placeholder="Paste Teams meeting URL (e.g., https://teams.microsoft.com/meet/44456859126019?p=...)"
                    className="input-field"
                  />
                  {isMOMAuthenticated && (
                    <div className="auth-status">
                      ✅ Authenticated with Teams
                      <button onClick={handleMOMLogout} className="logout-button">
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="input-section">
                  <label htmlFor="transcriptText">Paste your meeting transcript:</label>
                  <textarea
                    id="transcriptText"
                    value={transcriptText}
                    onChange={(e) => setTranscriptText(e.target.value)}
                    placeholder="Paste your meeting transcript here..."
                    rows="15"
                    className="textarea-field"
                    style={{width:'700px'}}
                  />
                </div>
              )}

              {error && (
                <div className="error-message">
                  <strong>Error:</strong> {error}
                </div>
              )}

              <div className="modal-actions">
                {inputMode === 'meeting_id' && !isMOMAuthenticated ? (
                  <button onClick={handleMOMLogin} className="auth-button">
                    Sign in to Teams to Generate MOM
                  </button>
                ) : (
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="generate-button"
                  >
                    {isGenerating ? 'Generating...' : 'Generate MOM'}
                  </button>
                )}
                <button onClick={handleReset} className="reset-button">
                  Reset
                </button>
              </div>
            </>
          ) : (
            <div className="result-section">
              <h3>Generated Minutes of Meeting</h3>
              
              {result.data && (
                <div className="mom-content">
                  <div className="mom-section">
                    <h4>Summary</h4>
                    <div className="summary-content">
                      {result.data.summary}
                    </div>
                  </div>

                  <div className="mom-section">
                    <h4>Action Items</h4>
                    <div className="action-items-list">
                      {result.data.action_items && result.data.action_items.length > 0 ? (
                        <ul>
                          {result.data.action_items.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p>No action items identified in this meeting.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button onClick={handleReset} className="generate-button">
                  Generate Another MOM
                </button>
                <button onClick={onClose} className="close-button-action">
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MOMGeneratorModalFull;